/**
 * XeroHeader — persistent context block beneath the page title (NOT a tab).
 * Connection pill, tenant name (click to copy), token-expiry chip (<7 days
 * only), and an actions menu (Open in Xero / Reconnect / Disconnect). Disconnect
 * sits behind an AlertDialog and is admin-only.
 */
import { useState } from 'react';
import { ExternalLink, Loader2, MoreHorizontal, Plug, Unplug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { ConnectionPill } from './ConnectionPill';
import { connectionPill, daysUntil, type XeroStatus } from '../lib/connection';
import { XERO_DASHBOARD_URL } from '../lib/links';

export function XeroHeader({
  status,
  isAdmin,
  onConnect,
  onDisconnect,
  connecting,
  disconnecting,
}: {
  status: XeroStatus;
  isAdmin: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  connecting: boolean;
  disconnecting: boolean;
}) {
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);
  const pill = connectionPill(status);
  const expiryDays = daysUntil(status.expiresAt);
  const showExpiryChip = expiryDays !== null && expiryDays < 7;

  const copyTenant = async () => {
    if (!status.tenantName) return;
    try {
      await navigator.clipboard.writeText(status.tenantName);
      toast.success('Organisation name copied');
    } catch {
      // Clipboard may be unavailable (insecure context); fail quietly.
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border bg-card px-4 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <ConnectionPill state={pill} />
        {status.tenantName && (
          <>
            <span className="h-3.5 w-px bg-border" aria-hidden="true" />
            <button
              type="button"
              onClick={copyTenant}
              className="text-sm font-medium hover:text-threadheads-purple"
              title="Copy organisation name"
            >
              {status.tenantName}
            </button>
          </>
        )}
        {showExpiryChip && (
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-[11px] font-medium',
              expiryDays !== null && expiryDays <= 0
                ? 'bg-red-100 text-red-700'
                : 'bg-amber-100 text-amber-700'
            )}
          >
            {expiryDays !== null && expiryDays <= 0
              ? 'Token expired'
              : `Token expires in ${Math.max(0, Math.round(expiryDays ?? 0))}d`}
          </span>
        )}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 px-2 text-xs gap-1.5">
            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only sm:not-sr-only">Actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem asChild>
            <a href={XERO_DASHBOARD_URL} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" aria-hidden="true" />
              Open in Xero
            </a>
          </DropdownMenuItem>
          {isAdmin && (
            <DropdownMenuItem onSelect={onConnect} disabled={connecting}>
              {connecting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              ) : (
                <Plug className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              Reconnect
            </DropdownMenuItem>
          )}
          {isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={(event) => {
                  event.preventDefault();
                  setConfirmDisconnectOpen(true);
                }}
              >
                <Unplug className="mr-2 h-4 w-4" aria-hidden="true" />
                Disconnect…
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmDisconnectOpen} onOpenChange={setConfirmDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Xero?</AlertDialogTitle>
            <AlertDialogDescription>
              Automatic COGS and stock-in-transit journals will stop posting, and manual pushes
              from PO and SO screens will be unavailable until you reconnect. Your synced accounts
              and history are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="border-orange-300 bg-transparent text-orange-700 hover:bg-orange-50 hover:text-orange-800"
              disabled={disconnecting}
              onClick={onDisconnect}
            >
              {disconnecting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              )}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
